import { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useApi } from '../services/api';
import { useAnalytics } from './useAnalytics';
import { getStoredRef } from '../utils/referral';
import { getStoredAttribution } from '../utils/attribution';

/**
 * Hook for handling Stripe checkout sessions
 * Extracted from Pricing/Main component for reuse across the app
 */
export const useStripeCheckout = () => {
  const { user } = useAuth0();
  const { callApi } = useApi();
  const { trackCheckoutStarted } = useAnalytics();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Start Stripe checkout session
   * @param {string} plan - Plan name ('Premium')
   * @param {boolean} isAnnual - Annual billing if true, monthly if false
   * @param {string} returnUrl - URL to return to after checkout
   * @param {boolean} withTrial - Include 7-day free trial
   * @param {string} promoCode - Optional promo code to pre-apply (e.g., 'WINBACK24')
   */
  const handleSubscribe = async (plan = 'Premium', isAnnual = false, returnUrl = null, withTrial = false, promoCode = null) => {
    setLoading(true);
    setError(null);

    try {
      // Validate user is authenticated before proceeding
      if (!user?.sub) {
        throw new Error('You must be logged in to subscribe. Please log in and try again.');
      }

      console.log('Starting checkout...', { plan, isAnnual, withTrial, promoCode, userId: user.sub });
      
      const referredByCode = getStoredRef();
      const attribution = getStoredAttribution();
      const response = await callApi('create-checkout-session', {
        method: 'POST',
        body: JSON.stringify({
          plan,
          isAnnual,
          userId: user.sub,
          returnUrl: returnUrl || `${window.location.origin}/account?upgrade=success`,
          withTrial,
          promoCode,
          referredByCode,
          attribution
        })
      });

      // Check if user is already subscribed
      if (response.status === 'error' && response.code === 'already_subscribed') {
        const shouldManage = window.confirm(
          `${response.message}\n\nWould you like to manage your subscription?`
        );
        if (shouldManage && response.action?.url) {
          window.location.href = response.action.url;
        }
        setLoading(false);
        return { success: false, alreadySubscribed: true };
      }

      // Normal checkout flow
      if (response.sessionUrl) {
        console.log('Got session URL:', response.sessionUrl);
        // checkout_started — fired right before leaving for Stripe's hosted checkout.
        const amount = isAnnual ? 199 : 42;
        trackCheckoutStarted(plan, isAnnual, amount, {
          with_trial: withTrial,
          promo_applied: !!promoCode
        });
        window.location.href = response.sessionUrl;
        return { success: true };
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Subscription error:', err);
      const message = err.message === 'Failed to fetch' 
        ? 'Unable to connect to the server. Please try again.'
        : err.message || 'An unexpected error occurred';
      setError(message);
      alert(`Failed to start checkout: ${message}`);
      setLoading(false);
      return { success: false, error: message };
    }
  };

  return {
    handleSubscribe,
    loading,
    error
  };
};

